import { IntlShape } from 'react-intl';

import getDatesToTranslations from '@/utils/getDatesToTranslations/getDatesToTranslations';
import Logger from '@/utils/logger';

import {
  EFaqsCategoryIcons,
  FAQ_CATEGORY_CONFIG,
  FAQ_TRANSLATION_PREFIX,
} from './config';
import { TFaqIconStat, TFAQsItem } from './types';

// INFO: Reverse lookup to map each `phraseSegment` back to its `EFaqsCategoryIcons` key.
const PHRASE_SEGMENT_TO_ICON: Record<string, EFaqsCategoryIcons> = Object
    .entries(FAQ_CATEGORY_CONFIG)
    .reduce<Record<string, EFaqsCategoryIcons>>(
        (acc, [icon, { phraseSegment }]) => {
        acc[phraseSegment] = icon as EFaqsCategoryIcons;
        return acc;
    }, {});

/**
 * Extracts the category icon from a FAQ translation key.
 *
 * Given `chatPage.servicesSection.exemplaryQuestions.buzzword_7`,
 * strips the prefix, isolates the segment before the last underscore (`buzzword`),
 * and resolves it to an `EFaqsCategoryIcons` value via reverse lookup.
 *
 * @param key - Full translation key
 * @returns The matching enum value, or `null` if the key doesn't match any known category
 */
const extractCategory = (key: string): EFaqsCategoryIcons | null => {
  const suffix = key.slice(FAQ_TRANSLATION_PREFIX.length);
  const lastUnderscore = suffix.lastIndexOf('_');

  if (lastUnderscore === -1) return null;

  const segment = suffix.slice(0, lastUnderscore);

  return PHRASE_SEGMENT_TO_ICON[segment] ?? null;
};

/**
 * Builds FAQ items by scanning `intl.messages` for keys matching `FAQ_TRANSLATION_PREFIX`.
 * Results are ordered by `FAQ_CATEGORY_CONFIG` definition order, then alphabetically within each category.
 * Keys with unrecognized categories are logged and skipped.
 *
 * @param intl - react-intl instance used for resolving translation keys
 * @returns FAQ items sorted by config-defined category order
 */
export const getMappedFaqs = (intl: IntlShape): TFAQsItem[] => {
  const dates = getDatesToTranslations(intl);

  // INFO: Precompute category order: phraseSegment → position index
  const categoryOrder = new Map(Object.values(FAQ_CATEGORY_CONFIG).map(({ phraseSegment }, index) => [phraseSegment, index]));

  return Object.keys(intl.messages)
    .filter((key) => key.startsWith(FAQ_TRANSLATION_PREFIX))
    .sort((a, b) => {
      const segA = a.slice(FAQ_TRANSLATION_PREFIX.length, a.lastIndexOf('_'));
      const segB = b.slice(FAQ_TRANSLATION_PREFIX.length, b.lastIndexOf('_'));

      return (categoryOrder.get(segA) ?? Infinity) - (categoryOrder.get(segB) ?? Infinity) || a.localeCompare(b);
    })
    .reduce<TFAQsItem[]>(
      (acc, key) => {
      const icon = extractCategory(key);

      if (!icon) {
        Logger.error('getMappedFaqs', 'Unknown FAQ category for key: ', key);
        return acc;
      }

      const { tooltipText } = FAQ_CATEGORY_CONFIG[icon];
      const name = intl.formatMessage({ id: key }, dates);

      acc.push({ id: key, name, icon, type: icon, tooltipText });
      return acc;
    }, []);
};

/**
 * Computes icon-level stats from a list of FAQ items.
 *
 * @param faqs - Array of FAQ items
 * @returns Array of aggregated stats per icon/type combination
 */
export const getFaqIconStats = (faqs: TFAQsItem[]): TFaqIconStat[] => {
  const statMap: Record<string, TFaqIconStat> = {};

  faqs.forEach((faq) => {
    const key = `${faq.icon}__${faq.type}`;

    if (!statMap[key]) {
      statMap[key] = {
        icon: faq.icon,
        type: faq.type,
        tooltipText: faq.tooltipText,
        size: 1,
      };
    } else {
      statMap[key].size += 1;
    }
  });

  return Object.values(statMap);
};